import classNames from 'classnames';
import { memo, forwardRef, useState, useEffect, useRef, type UIEvent } from 'react';
import { LocalizedService, RNDService, useLocalizedServiceFactory } from '../../../API/GoMoto';
import { Types, useCart, useCartDispatch } from '../../../context/cart';
import { useLocalization } from '../../../context/Localization/localization';
import { Snackbar } from '../../../controls/Snackbar/Snackbar/Snackbar';
import { Spacer } from '../../../controls/Spacer/Spacer';
import { isElectron } from '../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { formatAsCurrency } from '../../../helpers/Number';
import { getRndServiceDisplayName } from '../../../helpers/rndServiceDisplayName';
import { SpgPriceText } from '../../../controls/SpgPriceText/SpgPriceText';
import css from './ScrollableBody.module.scss';
import { GMAccessibilityButton } from '../../../controls/ButtonBase/index';
import { useLocation } from 'react-router-dom';
import { AppRoutePaths } from '../../../helpers/Routes';
import { Snack } from '../../../controls/Snackbar/Snackbar/Snackbar.types';
import type { ScrollableBodyProps } from './ScrollableBody.types';

export const ScrollableBody = memo(
    forwardRef<HTMLDivElement, ScrollableBodyProps>((props, ref) => {
        const cart = useCart();
        const cartDispatch = useCartDispatch();
        const Localization = useLocalization();
        const localizedServiceFactory = useLocalizedServiceFactory();
        const [snack, setSnack] = useState<Snack | undefined>();
        const location = useLocation();

        useEffect(() => {
            const serviceSnacks = cart.services.filter((filter) => filter.snackable)
                .map(localizedServiceFactory);

            let snack: Snack | undefined = undefined;

            if (serviceSnacks && serviceSnacks.length) {
                const service = serviceSnacks[0];
                const spgPricingText = (
                    <SpgPriceText
                        price={service.price}
                        priceLow={service.priceLow}
                        priceHigh={service.priceHigh}
                        spgPricing={service.spgPricing}
                        useSpgPricingLogic={!!service.useSpgPricing}
                    />
                );
                let description = <>{service.localization.name}</>;

                if (!!service.useSpgPricing && !!service.spgPricing && !!spgPricingText) {
                    description = (
                        <>
                            {description}{' - '}{spgPricingText}
                        </>
                    );
                }
                else if (service.isGenericUpsell) {
                    if (service.genericPrice1 && service.genericPrice2) {
                        if (service.genericPrice1 < service.genericPrice2) {
                            description = (
                                <>
                                    {description}{' - '}{formatAsCurrency(service.genericPrice1) + ' - ' + formatAsCurrency(service.genericPrice2)}
                                </>
                            );
                        }
                        else {
                            description = (
                                <>
                                    {description}{' - '}{formatAsCurrency(service.genericPrice2) + ' - ' + formatAsCurrency(service.genericPrice1)}
                                </>
                            );
                        }
                    }
                    else if (service.genericPrice1) {
                        description = (
                            <>
                                {description}{' - '}{formatAsCurrency(service.genericPrice1)}
                            </>
                        );
                    }
                    else if (service.genericPrice2) {
                        description = (
                            <>
                                {description}{' - '}{formatAsCurrency(service.genericPrice2)}
                            </>
                        );
                    }
                }
                else {
                    if (service.price) {
                        description = (
                            <>
                                {description}{' - '}{formatAsCurrency(service.price)}
                            </>
                        );
                    }
                }

                snack = {
                    /*
                     * unique key so repeatedly clicking on a service does not
                     * leave the snackbar in a stuck state
                     */
                    key: new Date()
                        .getTime() + '-' + service.id,
                    onClose: () => {
                        setSnack(undefined);
                        cartDispatch({
                            type: Types.RemoveServiceSnack,
                            payload: service.id,
                        });
                    },
                    label: Localization.ServiceAdded,
                    description: description,
                };
            }

            if (!snack) {
                const maintMenuSnacks = cart.maintMenus.filter((filter) => filter.snackable);

                if (maintMenuSnacks && maintMenuSnacks.length) {
                    const menu = maintMenuSnacks[0];
                    const description = (
                        <>
                            {`${menu.title} - `}
                            <SpgPriceText
                                priceLow={menu.priceLow}
                                priceHigh={menu.priceHigh}
                                spgPricing={menu.spgPricing}
                                showSingleWhenNoRange={false}
                            />
                        </>
                    );

                    snack = {
                        key: new Date()
                            .getTime() + '-' + menu.id + '-' + menu.scheduleOption,
                        onClose: () => {
                            setSnack(undefined);
                            cartDispatch({
                                type: Types.RemoveMenuSnack,
                                payload: menu,
                            });
                        },
                        label: Localization.ServiceAdded,
                        description: description,
                    };
                }
            }

            if (!snack) {
                const rndServiceSnacks: RNDService[] = cart.rndServices.filter((filter) => filter.snackable && !filter.doneElsewhere);

                if (rndServiceSnacks && rndServiceSnacks.length) {
                    const rndService: RNDService = rndServiceSnacks[0];
                    const localizedService: LocalizedService | undefined = rndService.linkedService ? localizedServiceFactory(rndService.linkedService) : undefined;
                    const pricingText = (
                        <SpgPriceText
                            price={rndService.price ?? localizedService?.price}
                            priceLow={localizedService?.priceLow}
                            priceHigh={localizedService?.priceHigh}
                            spgPricing={localizedService?.useSpgPricing ? localizedService?.spgPricing : undefined}
                        />
                    );

                    const description = (
                        <>
                            {getRndServiceDisplayName(rndService, localizedService)}{' - '}{pricingText}
                        </>
                    );

                    snack = {
                        key: new Date()
                            .getTime() + '-' + rndService.opCode,
                        onClose: () => {
                            setSnack(undefined);
                            cartDispatch({
                                type: Types.RemoveRNDSnack,
                                payload: rndService,
                            });
                        },
                        label: Localization.ServiceAdded,
                        description: description,
                    };
                }
            }

            setSnack(snack);
        }, [Localization.ServiceAdded, cart.services, cart.maintMenus, cart.rndServices, localizedServiceFactory, cartDispatch, Localization.AskUsAboutPricing]);

        useEffect(() => {
            const serviceSnacks = cart.xtime.services.filter((filter) => filter.snackable);

            let snack: Snack | undefined = undefined;

            if (serviceSnacks && serviceSnacks.length) {
                const service = serviceSnacks[0];
                let description = <>{service.name}</>;

                if (service.price) {
                    description = (
                        <>
                            {description}{' - '}{formatAsCurrency(service.price)}
                        </>
                    );
                }

                snack = {
                    /*
                     * unique key so repeatedly clicking on a service does not
                     * leave the snackbar in a stuck state
                     */
                    key: new Date()
                        .getTime() + '-' + service.id,
                    onClose: () => {
                        setSnack(undefined);
                        cartDispatch({
                            type: Types.RemoveXtimeServiceSnack,
                            payload: service.id,
                        });
                    },
                    label: Localization.ServiceAdded,
                    description: description,
                };
            }

            if (!snack) {
                const maintMenuSnacks = cart.xtime.menus.filter((filter) => filter.snackable);

                if (maintMenuSnacks && maintMenuSnacks.length) {
                    const menu = maintMenuSnacks[0];
                    const description = (
                        <>
                            {menu.name}{' - '}{menu.totalPrice}
                        </>
                    );

                    snack = {
                        key: new Date()
                            .getTime() + '-' + menu.id,
                        onClose: () => {
                            setSnack(undefined);
                            cartDispatch({
                                type: Types.RemoveXtimeMenuSnack,
                                payload: menu,
                            });
                        },
                        label: Localization.ServiceAdded,
                        description: description,
                    };
                }
            }

            setSnack(snack);
        }, [Localization.ServiceAdded, cart.xtime.services, cart.xtime.menus, cartDispatch, Localization.AskUsAboutPricing]);

        // forcefully stop reseting the scroll position to zero
        const scrollPos = useRef(0);
        const scrollHandler = (e: UIEvent<HTMLDivElement>) => {
            e.preventDefault();
            if (e.currentTarget.scrollTop === 0) {
                e.currentTarget.scrollTop = scrollPos.current;
            }
            else if (e.currentTarget.scrollTop !== scrollPos.current) {
                scrollPos.current = e.currentTarget.scrollTop;
            }
        };

        return (
            <>
                <Snackbar snack={snack} />
                <div
                    id={'scrollable-body'}
                    data-testid={'scrollable-body'}
                    onScroll={scrollHandler}
                    ref={ref}
                    className={classNames(css.scrollableBody, {
                        [css.unset]: isElectron() && props.unsetButtons,
                        [css.removeOverflow]: props.disableOverflow,
                    })}
                >
                    {!props.hideAccessibilityButton && (isElectron() || (location.pathname !== AppRoutePaths.Accessibility && <GMAccessibilityButton />))}
                    {props.heading}
                    {props.topDivider && isElectron() ? <Spacer /> : null}
                    {props.children}
                </div>
                {isElectron() && !props.hideSpacer ? <Spacer /> : null}
                {props.footing ? <div className={css.footing}>{props.footing}</div> : null}
                <div onClick={() => snack?.onClose?.()}>{props.proceedButtons}</div>
            </>
        );
    }),
);
